/**
 * @file index.tsx
 *
 * @fileoverview Component for showing cards in a slider in a landing page row.
 */

import React from 'react';
import { CardsSliderParagraph, Paragraph } from '~/jobiqo/types/types';
import Slide from '~/vcl/molecules/15-slider';
import CardWrapper from '~/jobiqo/components/Landingpage/CardsSlider/components/CardWrapper';
import { Text } from '~/vcl/atoms/13-text/03-text';
import { Box } from '~/vcl/atoms/07-box';
import { Main } from '~/vcl/atoms/06-layout/09-main';
import { Spacing } from '~/vcl/atoms/06-layout/07-spacing';
import { getFrontendTheme } from '~/jobiqo/utils/getFrontend';
import { useFrontendConfig } from '~/jobiqo/context/frontendConfig';

interface CardsSliderProps {
  rowCardsSliderInfo: CardsSliderParagraph;
  maxContainerSize?: 'small' | 'medium' | 'large' | 'xLarge';
}

export const CardsSlider = ({
  rowCardsSliderInfo,
  maxContainerSize,
}: CardsSliderProps) => {
  const frontendConfig = useFrontendConfig();
  const frontendTheme = getFrontendTheme(frontendConfig);
  const cards: React.ReactNode[] =
    rowCardsSliderInfo.items && rowCardsSliderInfo.items.length > 0
      ? rowCardsSliderInfo.items
          .filter((c) => !!c)
          .map((card, index) => {
            return <CardWrapper key={index} card={card as Paragraph} />;
          })
      : [];
  const SHOW_DOTS = frontendTheme.LANDING_PAGES.CARDS_SLIDER.SHOW_DOTS;

  return (
    <Box
      boxShadow="none"
      borderRadius="0px"
      margin={0}
      padding={0}
      backgroundColorOverride={
        rowCardsSliderInfo.backgroundColor
          ? `var(--color-${rowCardsSliderInfo.backgroundColor})`
          : 'transparent'
      }
    >
      <Main {...(maxContainerSize ? { size: maxContainerSize } : null)}>
        <Spacing
          paddingTop={rowCardsSliderInfo.spacing ?? 11}
          paddingBottom={rowCardsSliderInfo.spacing ?? 12}
          margin={0}
        >
          {rowCardsSliderInfo.cardsSliderTitle && (
            <>
              <Text
                textAlign="center"
                tag="h2"
                fontSize={[5, 7]}
                lineHeight={[5, 7]}
              >
                {rowCardsSliderInfo.cardsSliderTitle}
              </Text>
              <Spacing size="large" />
            </>
          )}
          {rowCardsSliderInfo.text && (
            <>
              <Text
                textAlign="center"
                color="gray6"
                fontSize={4}
                lineHeight={5}
              >
                {rowCardsSliderInfo.text}
              </Text>
              <Spacing marginBottom={11} />
            </>
          )}
          <Spacing marginBottom={11} />
          <Slide
            slides={cards.map((card, index) => (
              <Spacing
                key={index}
                marginRight={4}
                marginLeft={4}
                marginTop={0}
                marginBottom={0}
              >
                {card}
              </Spacing>
            ))}
            slidesToShow={1}
            dots={SHOW_DOTS}
          />
        </Spacing>
      </Main>
    </Box>
  );
};

export default CardsSlider;
